import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { useAuthStore } from '@store/authStore';
import {
    loginSchema,
    LoginFormData,
    idLoginSchema,
    IdLoginFormData,
} from '@/schemas/loginSchema';
import Tabs from '@components/common/Tabs';
import NaverLoginButton from '@components/common/Login/Naver/NaverLoginButton';
import KakaoLoginButton from '@components/common/Login/Kakao/Kakaologinbutton';
import GoogleLoginButton from '@components/common/Login/Google/Googleloginbutton';

const STORAGE_KEY_TAB = 'login_last_tab';
const STORAGE_KEY_SNS = 'login_last_sns';

type TabId = 'company-login' | 'admin-login';
type SnsType = 'naver' | 'kakao' | 'google' | null;

const Login = () => {
    const adminIdRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showIdForm, setShowIdForm] = useState(false);
    const [showIdPw, setShowIdPw] = useState(false);

    const [activeTab, setActiveTab] = useState<TabId>(
        () =>
            (localStorage.getItem(STORAGE_KEY_TAB) as TabId) ?? 'company-login',
    );
    const [lastSns, setLastSns] = useState<SnsType>(
        () => localStorage.getItem(STORAGE_KEY_SNS) as SnsType,
    );

    const {
        login,
        idLogin,
        naverLogin,
        kakaoLogin,
        googleLogin,
        isLoading,
        isAuthenticated,
        checkTokenExpiry,
    } = useAuthStore();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: 'onChange',
    });

    const {
        register: registerIdForm,
        handleSubmit: handleIdSubmit,
        formState: { errors: idErrors },
        setError: setIdError,
    } = useForm<IdLoginFormData>({
        resolver: zodResolver(idLoginSchema),
        mode: 'onChange',
    });

    const handleTabChange = (tabId: string) => {
        const id = tabId as TabId;
        setActiveTab(id);
        localStorage.setItem(STORAGE_KEY_TAB, id);
    };

    const saveLastSns = (sns: SnsType) => {
        if (!sns) return;
        setLastSns(sns);
        localStorage.setItem(STORAGE_KEY_SNS, sns);
    };

    useEffect(() => {
        // ✅ 네이버 콜백 - URL 해시에 access_token
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
            const params = new URLSearchParams(hash.replace('#', ''));
            const accessToken = params.get('access_token');
            console.log('accessToken : ' + accessToken);
            if (accessToken) {
                window.history.replaceState(null, '', window.location.pathname);
                naverLogin(accessToken).then(result => {
                    console.log(result);
                    if (!result.success) return;
                    if (result.isJoined) {
                        saveLastSns('naver');
                        navigate('/user/dashboard');
                    } else {
                        // 미가입 → 회원가입 페이지
                        navigate('/join/sns', {
                            state: { snsInfo: result.snsInfo },
                        });
                    }
                });
                return;
            }
        }

        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state'); // 구글: 'google' / 카카오: null

        if (error) {
            window.history.replaceState(null, '', window.location.pathname);
            return;
        }

        if (code) {
            window.history.replaceState(null, '', window.location.pathname);

            // ── 구글
            if (state === 'google') {
                googleLogin(code).then(result => {
                    if (!result.success) return;
                    if (result.isJoined) {
                        saveLastSns('google');
                        navigate('/user/dashboard');
                    } else {
                        navigate('/join/sns', {
                            state: { snsInfo: result.snsInfo },
                        });
                    }
                });
            } else {
                // ── 카카오
                kakaoLogin(code).then(result => {
                    if (!result.success) return;
                    if (result.isJoined) {
                        saveLastSns('kakao');
                        navigate('/user/dashboard');
                    } else {
                        navigate('/join/sns', {
                            state: { snsInfo: result.snsInfo },
                        });
                    }
                });
            }
            return;
        }

        const isTokenValid = checkTokenExpiry();
        if (isAuthenticated && isTokenValid) {
            navigate('/dashboard');
            return;
        }

        if (activeTab === 'admin-login' && adminIdRef.current) {
            adminIdRef.current.focus();
        }
    }, []);

    const onSubmit = async (data: LoginFormData) => {
        if (isLoading) return;
        const result = await login({
            adminId: data.adminId.trim(),
            adminPw: data.adminPw.trim(),
        });
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError('root', {
                type: 'manual',
                message: result.error || '로그인에 실패했습니다.',
            });
        }
    };

    const onIdSubmit = async (data: IdLoginFormData) => {
        if (isLoading) return;
        const result = await idLogin(data.userId.trim(), data.userPw.trim());
        if (result.success) {
            navigate('/user/dashboard');
        } else {
            setIdError('root', {
                type: 'manual',
                message: result.error || '로그인에 실패했습니다.',
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSubmit(onSubmit)();
    };

    const togglePasswordVisibility = () => setShowPassword(prev => !prev);

    const RecentBadge = () => (
        <span
            style={{
                position: 'absolute',
                top: '14px',
                right: '8px',
                zIndex: 1,
                padding: '1px 7px',
                fontSize: '10px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: '#ff5c5c',
                borderRadius: '8px',
                pointerEvents: 'none',
            }}
        >
            최근
        </span>
    );

    const adminLoginContent = (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="row gy-3">
                <div className="col-xl-12">
                    <label className="form-label text-white" htmlFor="admin_id">
                        Admin ID<sup className="fs-12 text-danger">*</sup>
                    </label>
                    <input
                        {...register('adminId')}
                        ref={e => {
                            register('adminId').ref(e);
                            adminIdRef.current = e;
                        }}
                        className={`form-control ${errors.adminId ? 'is-invalid' : ''}`}
                        id="admin_id"
                        placeholder="Enter your admin id"
                        type="text"
                        onKeyDown={handleKeyPress}
                        disabled={isLoading}
                        autoComplete="username"
                    />
                    {errors.adminId && (
                        <div className="invalid-feedback text-white">
                            {errors.adminId.message}
                        </div>
                    )}
                </div>

                <div className="col-xl-12">
                    <label className="form-label text-white" htmlFor="admin_pw">
                        Password<sup className="fs-12 text-danger">*</sup>
                    </label>
                    <div className="input-group">
                        <input
                            {...register('adminPw')}
                            className={`form-control ${errors.adminPw ? 'is-invalid' : ''}`}
                            id="admin_pw"
                            placeholder="Enter your password"
                            type={showPassword ? 'text' : 'password'}
                            onKeyDown={handleKeyPress}
                            disabled={isLoading}
                            autoComplete="current-password"
                        />
                        <button
                            className="btn btn-primary show-password-button"
                            type="button"
                            onClick={togglePasswordVisibility}
                            disabled={isLoading}
                            aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                            }
                        >
                            <i
                                className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} align-middle`}
                            />
                        </button>
                        {errors.adminPw && (
                            <div className="invalid-feedback text-white">
                                {errors.adminPw.message}
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-xl-12">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={e => setRememberMe(e.target.checked)}
                            disabled={isLoading}
                        />
                        <label
                            className="form-check-label text-white fw-normal fs-12"
                            htmlFor="rememberMe"
                        >
                            Remember me
                        </label>
                    </div>
                </div>
            </div>

            {errors.root && (
                <div className="alert alert-danger mt-3 py-2 text-center">
                    {errors.root.message}
                </div>
            )}

            <div className="d-grid mt-4">
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                            Signing In...
                        </>
                    ) : (
                        <>
                            <i className="ri-login-circle-line lh-1 me-2 align-middle" />
                            Sign In
                        </>
                    )}
                </button>
            </div>
        </form>
    );

    const searchParams = new URLSearchParams(window.location.search);
    const isNaverCallback = window.location.hash.includes('access_token');
    const isGoogleCallback =
        searchParams.has('code') && searchParams.get('state') === 'google';
    const isKakaoCallback =
        searchParams.has('code') && searchParams.get('state') !== 'google';
    const isSocialCallback =
        isNaverCallback || isKakaoCallback || isGoogleCallback;

    const spinnerColor = isNaverCallback
        ? 'text-success'
        : isGoogleCallback
          ? 'text-danger'
          : 'text-warning';

    const companyLoginContent = (
        <div className="py-3">
            {isSocialCallback ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-4 gap-2">
                    <div
                        className={`spinner-border ${spinnerColor}`}
                        role="status"
                    >
                        <span className="visually-hidden">
                            로그인 처리 중...
                        </span>
                    </div>
                    <p className="text-white op-7 fs-13 mb-0">
                        로그인 처리 중...
                    </p>
                </div>
            ) : showIdForm ? (
                /* ── ID/PW 폼 패널 ── */
                <div className={styles.idFormPanel}>
                    <button
                        type="button"
                        className={styles.idFormBack}
                        onClick={() => setShowIdForm(false)}
                        disabled={isLoading}
                    >
                        <i className="ri-arrow-left-s-line" />
                        SNS 로그인으로 돌아가기
                    </button>

                    <form onSubmit={handleIdSubmit(onIdSubmit)} noValidate>
                        <div className="row gy-3">
                            <div className="col-12">
                                <div className={styles.idInputWrapper}>
                                    <i
                                        className={`ri-user-3-line ${styles.idInputIconLeft}`}
                                    />
                                    <input
                                        {...registerIdForm('userId')}
                                        className={`form-control ${styles.idInput} ${idErrors.userId ? 'is-invalid' : ''}`}
                                        placeholder="아이디"
                                        type="text"
                                        disabled={isLoading}
                                        autoComplete="username"
                                    />
                                </div>
                                {idErrors.userId && (
                                    <div className={styles.idFieldError}>
                                        {idErrors.userId.message}
                                    </div>
                                )}
                            </div>
                            <div className="col-12">
                                <div className={styles.idInputWrapper}>
                                    <i
                                        className={`ri-lock-2-line ${styles.idInputIconLeft}`}
                                    />
                                    <input
                                        {...registerIdForm('userPw')}
                                        className={`form-control ${styles.idInput} ${idErrors.userPw ? 'is-invalid' : ''}`}
                                        placeholder="비밀번호"
                                        type={showIdPw ? 'text' : 'password'}
                                        disabled={isLoading}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.idEyeBtn}
                                        onClick={() => setShowIdPw(p => !p)}
                                        disabled={isLoading}
                                        tabIndex={-1}
                                    >
                                        <i
                                            className={
                                                showIdPw
                                                    ? 'ri-eye-line'
                                                    : 'ri-eye-off-line'
                                            }
                                        />
                                    </button>
                                </div>
                                {idErrors.userPw && (
                                    <div className={styles.idFieldError}>
                                        {idErrors.userPw.message}
                                    </div>
                                )}
                            </div>
                        </div>

                        {idErrors.root && (
                            <div className={styles.idRootError}>
                                <i className="ri-error-warning-line me-1" />
                                {idErrors.root.message}
                            </div>
                        )}

                        <div className="d-grid mt-4">
                            <button
                                type="submit"
                                className={`btn btn-primary ${styles.idSubmitBtn}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        로그인 중...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-login-circle-line me-2" />
                                        로그인
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* ── SNS 로그인 패널 ── */
                <>
                    <p className="text-center text-white op-7 fs-13 mb-3">
                        SNS 계정으로 간편하게 로그인하세요.
                    </p>
                    <div className="mb-2" style={{ position: 'relative' }}>
                        {lastSns === 'naver' && <RecentBadge />}
                        <NaverLoginButton />
                    </div>
                    <div className="mb-2" style={{ position: 'relative' }}>
                        {lastSns === 'kakao' && <RecentBadge />}
                        <KakaoLoginButton />
                    </div>
                    <div style={{ position: 'relative' }}>
                        {lastSns === 'google' && <RecentBadge />}
                        <GoogleLoginButton />
                    </div>

                    <div className={styles.orDivider}>
                        <span>또는</span>
                    </div>

                    <button
                        type="button"
                        className={styles.idLoginBtn}
                        onClick={() => setShowIdForm(true)}
                    >
                        <i className="ri-id-card-line" />
                        ID로 로그인하기
                    </button>
                </>
            )}
        </div>
    );

    const tabs = [
        {
            id: 'company-login',
            label: '대행사',
            content: companyLoginContent,
            isNoPadding: true,
        },
        {
            id: 'admin-login',
            label: '관리자',
            content: adminLoginContent,
            isNoPadding: true,
        },
    ];

    return (
        <div>
            <video
                className={styles.videoBackground}
                autoPlay
                muted
                loop
                playsInline
            >
                <source src="/video/bg_video_1.mp4" type="video/mp4" />
            </video>

            <div className={styles.videoOverlay} />

            <div className="container-lg">
                <div className="row justify-content-center authentication authentication-basic align-items-center h-100">
                    <div className="col-xxl-4 col-md-6 col-sm-8 col-12">
                        <div
                            className={`card custom-card my-4 ${styles.cardLogin}`}
                        >
                            <div className="card-body">
                                <div className="row mx-0 align-items-center">
                                    <div className="p-3">
                                        <div className="text-center">
                                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                                <img
                                                    className={styles.loginLogo}
                                                    src="/images/common/logo/logo_lite.png"
                                                    alt="AIS Logo"
                                                />
                                            </h2>
                                            <p className="mb-4 text-white op-7 fw-normal text-center">
                                                Smart Farming Management System.
                                            </p>
                                        </div>

                                        <Tabs
                                            tabs={tabs}
                                            tabStyle="login nav-tabs tab-style-2 nav-justified mb-3 d-sm-flex d-block nav"
                                            defaultActiveTab={activeTab}
                                            onTabChange={handleTabChange}
                                        />

                                        <div className="text-center">
                                            <p className="text-white mt-3 mb-0">
                                                If you are curious about AIS,{' '}
                                                <a
                                                    className="text-green fw-medium text-decoration-underline"
                                                    href="https://www.aiskorea.co.kr"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    click here
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
